import throttle from "lodash/throttle"
import { useEffect, useState } from "react"
import { dispatchEvent } from "../utils/dispatcher"
import useIsListening from "./useIsListening"

// Use MutationObserver to dispatch events to the document when
// new nodes enter the DOM

// emit event, i.e. step:content:view:rendered
const useDocumentEventDispatcher = (dispatchKey) => {
  const [node, setNode] = useState()
  const isListening = useIsListening(dispatchKey)

  useEffect(() => {
    // Throttle is necessary in some cases where calling refTagger.tag()
    // results in subsequent DOM mutations (neverending loop)
    const emit = throttle(() => {
      dispatchEvent(dispatchKey, null, {
        bubbles: true
      })
    }, 500)

    const observe = () => {
      const config = {
        childList: true,
        subtree: true
      }
      const callback = (mutationsList) => {
        const childAdded = mutationsList.find(
          (m) =>
            m.type == "childList" &&
            m.addedNodes.length > 0 &&
            // Ignoring all added nodes of type "text".
            // It should be safe to assume any added nodes we care about will append
            // their text content at the same time when they are added.
            // Ultimately, the main consumers of this "emit" fn are cases where schools
            // are using Reftagger to tag verse references entering the DOM.
            // When Reftagger tags verses, it appends text nodes, which we do not
            // want to respond to! Hence, we ignore.
            Array.from(m.addedNodes).find((node) => node.nodeType !== 3)
        )

        if (childAdded) {
          // A child node has been added
          emit()
        }
      }
      const observer = new MutationObserver(callback)
      observer.observe(node, config)
      return observer
    }

    if (node && isListening) {
      const observer = observe()
      return () => observer.disconnect()
    }
  }, [node, isListening])

  return setNode
}

export default useDocumentEventDispatcher
